import { Component, Prop, State, h } from '@stencil/core';
import IconArrowLeft from '../../assets/svg/arrow-left.svg'
import IconQuoteOpen from '../../assets/svg/quote-open.svg'

import {Source, Review, ReviewType, SortingOption} from "./interfaces"
import Helper from '../../Helpers/Helper';


@Component({
  tag: 'salla-reviews',
  styleUrl: 'salla-reviews.scss',
})
export class SallaReviews {
  constructor(){
    this.source = this.source;
    salla.onReady(() => {
      this.displayAllURL = salla.url.get('testimonials')
      this.isRTL = salla.config.get('theme.is_rtl', true)
    })

    salla.lang.onLoaded(() => {  
      this.testimonialText = salla.lang.get('blocks.home.testimonials')
      this.displayAllLinkText = salla.lang.get('blocks.home.display_all')
    })
  }

  /**
   * Controls the visibility of a link to the reviews page.
   * 
   * When set to `true`, a link will be displayed allowing users to navigate to the full reviews section.
   * 
   * @type {boolean}
   */
  @Prop({ reflect: true }) displayAllLink: boolean

  /**
   * Specifies the data source for reviews.
   * 
   * Valid options include:
   *  - 'categories': Retrieves reviews for specific product categories.
   *  - 'products': Retrieves reviews for individual products.
   *  - 'json': Retrieves reviews from a custom JSON payload provided in `sourceValue`.
   * 
   * When using 'json' as the source, a valid payload must be provided in `sourceValue`.
   * 
   * @type {Source}
   * @default "store"
   */
  @Prop({ reflect: true }) source?: Source;

  /**
   * Provides data specific to the chosen source.
   * 
   * Required when using 'categories', 'products', or 'json' as the source:
   *   - 'categories': An array of category IDs.
   *   - 'products': An array of product IDs.
   *   - 'json': A custom JSON object containing review data.
   * 
   * @type {string | object | Array<number>}
   */
  @Prop({ reflect: true }) sourceValue?: string | object | Array<number>;

  /**
   * Defines the maximum number of reviews to retrieve from the API.
   * 
   * @type {number}
   * @default 5
   */
  @Prop({ reflect: true }) limit = 5


  /**
   * Specifies the type of reviews to fetch.
   * Available options:
   * - "all": Fetches reviews from all sources.
   * - "store": Fetches reviews specific to the store.
   * - "products": Fetches reviews specific to products.
   * 
   * @type {ReviewType}
   * @default store
   */
  @Prop() type: ReviewType = "store";


  /**
   * Specifies the sorting criteria for the fetched reviews.
   * Available options:
   * - "top_rating": Sorts reviews based on top ratings.
   * - "random": Sorts reviews randomly.
   * - "latest": Sorts reviews based on the latest ones (default).
   * 
   * @type {SortingOption}
   * @default latest
   */
  @Prop() sort: SortingOption = "latest";


  /**
   * Specifies whether to hide customer information in the component.
   * When set to true, customer information will be hidden.
   * Defaults to false, meaning customer information will be displayed.
   */
  @Prop() hideCustomerInfo: boolean = false;


  @State() reviews: Array<Review> = [];
  @State() isRTL: boolean
  @State() showReviews: boolean = false;
  @State() testimonialText: string = salla.lang.get('blocks.home.testimonials');
  @State() displayAllLinkText: string = salla.lang.get('blocks.home.display_all');

  private displayAllURL = null

  fetchReviews() {
    if (this.source === 'json') {
      return Promise.resolve(JSON.parse(this.sourceValue as string));
    }
    const isJsonEncoded = ['products', 'categories'].includes(this.source);
    const params = {
      limit: this.limit,
      source: this.source,
      items: isJsonEncoded ? JSON.parse(this.sourceValue as string) : this.sourceValue,
      sort: this.sort,
      type: this.type,
      hide_customer_info: this.hideCustomerInfo?1:0
    };
    return salla.api.request('reviews', { params }, 'get');
  }


  componentWillLoad() {
    return (new Promise(resolve => salla.onReady(resolve)))
    .then(() => this.fetchReviews())
    .then((resp) => resp.data || [])
    .then(reviews => {
      if (reviews.length) {
        this.reviews = reviews;
        this.showReviews = true;
        Helper.generateReviewSchema(this.reviews);
      }
    });
  }

  render() {
    return (
      <div class="s-reviews-container">
        <div class="s-reviews-header-wrapper">
          <h1 class="s-reviews-header">
            {this.testimonialText}
          </h1>
          {!!this.displayAllLink ? (
            <a href={this.displayAllURL} class="s-reviews-display-all">
              {this.displayAllLinkText}
              <span class="s-reviews-display-all-icon" innerHTML={IconArrowLeft} />
            </a>
          ) : null}
        </div>
        <salla-slider
          centered
          slides-per-view={1}
          type="testimonials"
          class="s-reviews-testimonials-slider"
          controls-outer
          auto-play
          id={`testimonials-${this.source}-slider`}
        >
          <div slot="items">
            {this.reviews.map((item: Review, index) => (
              <div key={index} class="s-reviews-swiper-slide">
                <div class="s-reviews-testimonial">
                  <div class="s-reviews-testimonial__inner">
                    <div class="s-reviews-testimonial__avatar">
                      <img
                        src={'images/s-empty.png'}
                        data-src={item.avatar}
                        alt={item.name ? item.name : 'testimonial-' + index}
                        class="lazy"
                      />
                    </div>
                    <div class="s-reviews-testimonial__text">
                      <p innerHTML={item.content}></p>
                      <div class="s-reviews-testimonial__name_wrapper">
                        <div class="s-reviews-testimonial__info">
                          {item.name && <h2>{`${item.name}`}</h2>}
                        </div>
                        <div class="s-reviews-testimonial__rating">
                          {/* TODO stars is missing */}
                          <salla-rating-stars size="small" value={item.stars}></salla-rating-stars>
                        </div>
                      </div>
                    </div>

                    <span class="s-reviews-testimonial__icon" innerHTML={IconQuoteOpen} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </salla-slider>
      </div>
    );
  }

}

